"use client";

// The palette's four result groups. Presentational — every list handed in is
// already narrowed (records server-side, the other two by the palette itself).
// Split out of CommandPalette.tsx (module-size).

import { ArrowRight, CornerDownLeft, Plus, Search } from "lucide-react";
import { CommandGroup, CommandItem, CommandSeparator } from "@/components/ui/command";
import { SearchResultIcon } from "@/lib/hooks/algolia-search/SearchResultIcon";
import type { SearchResult } from "@/lib/hooks/algolia-search/types";
import type { CreateAction } from "@/components/ui/navigation/enhanced-quick-actions/create-actions";
import type { PaletteDestination } from "./flatten-navigation";

export function RecordResultsGroup({
  records,
  onSelect,
}: {
  records: SearchResult[];
  onSelect: (result: SearchResult) => void;
}) {
  if (records.length === 0) return null;
  return (
    <CommandGroup heading="Records">
      {records.map((result) => (
        <CommandItem
          key={`${result.type}-${result.objectID}`}
          value={`record-${result.objectID}`}
          onSelect={() => onSelect(result)}
          className="gap-3"
        >
          <SearchResultIcon type={result.iconType || result.type} />
          <span className="min-w-0 flex-1 truncate">
            {result.title || result.name || "Untitled"}
            {result.subtitle && (
              <span className="ml-2 text-xs text-muted-foreground">{result.subtitle}</span>
            )}
          </span>
          <span className="shrink-0 text-[11px] uppercase tracking-wide text-muted-foreground">
            {result.type}
          </span>
        </CommandItem>
      ))}
    </CommandGroup>
  );
}

export function DestinationsGroup({
  destinations,
  separated,
  onSelect,
}: {
  destinations: PaletteDestination[];
  separated: boolean;
  onSelect: (destination: PaletteDestination) => void;
}) {
  if (destinations.length === 0) return null;
  return (
    <>
      {separated && <CommandSeparator />}
      <CommandGroup heading="Go to">
        {destinations.map((destination) => (
          <CommandItem
            key={destination.id}
            value={`goto-${destination.id}`}
            onSelect={() => onSelect(destination)}
            className="gap-3"
          >
            <ArrowRight className="h-4 w-4 text-muted-foreground" aria-hidden />
            <span className="min-w-0 flex-1 truncate">
              {destination.name}
              {destination.path && (
                <span className="ml-2 text-xs text-muted-foreground">{destination.path}</span>
              )}
            </span>
          </CommandItem>
        ))}
      </CommandGroup>
    </>
  );
}

export function CreateActionsGroup({
  actions,
  onSelect,
}: {
  actions: CreateAction[];
  onSelect: (action: CreateAction) => void;
}) {
  if (actions.length === 0) return null;
  return (
    <>
      <CommandSeparator />
      <CommandGroup heading="Create">
        {actions.map((action) => {
          const Icon = action.icon;
          return (
            <CommandItem
              key={action.key}
              value={`create-${action.key}`}
              onSelect={() => onSelect(action)}
              className="gap-3"
            >
              <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
              <span className="min-w-0 flex-1 truncate">
                New {action.label.toLowerCase()}
                <span className="ml-2 text-xs text-muted-foreground">{action.desc}</span>
              </span>
              <Plus className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
            </CommandItem>
          );
        })}
      </CommandGroup>
    </>
  );
}

export function FullSearchGroup({
  trimmedQuery,
  onSelect,
}: {
  trimmedQuery: string;
  onSelect: () => void;
}) {
  if (trimmedQuery.length === 0) return null;
  return (
    <>
      <CommandSeparator />
      <CommandGroup heading="Search">
        <CommandItem value="full-search" onSelect={onSelect} className="gap-3">
          <Search className="h-4 w-4 text-muted-foreground" aria-hidden />
          <span className="min-w-0 flex-1 truncate">
            See all results for &ldquo;{trimmedQuery}&rdquo;
          </span>
          <CornerDownLeft className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        </CommandItem>
      </CommandGroup>
    </>
  );
}
